/**
 * SSE (Server-Sent Events) Streaming Utilities
 * Handles real-time AI response streaming from backend
 * 
 * Primary: Lambda Function URL (15-min streaming, no timeouts)
 * Fallback: WebSocket (for notifications only)
 */

const API_BASE_URL = (import.meta.env?.VITE_API_URL || '/api').replace(/\/$/, '');
const _AI_HTTP_ENDPOINT = `${API_BASE_URL}/v1/ai/chat`;
const AI_FUNCTION_URL = import.meta.env?.VITE_AI_FUNCTION_URL ? String(import.meta.env.VITE_AI_FUNCTION_URL).trim() : '';
const CONFIGURED_WS_URL = import.meta.env?.VITE_AI_WS_URL ? String(import.meta.env.VITE_AI_WS_URL).trim() : '';
// WebSocket URL fallbacks removed - use VITE_AI_WS_URL environment variable instead
const DEV_WS_FALLBACKS: Record<string, string> = {};

import { logger } from '../utils/logger';
import type { DocumentOperation } from '../types/document-operations';
import type { AeroDocument, AeroSection } from '../types/document-schema';
import type { DiagramState } from '../types/diagram-types';

export interface StreamEvent {
  type: 'init' | 'progress' | 'tool_start' | 'tool_complete' | 'content' | 'warning' | 'error' | 'done' | 'title_update' | 'keepalive' | 'handoff' | 'edit_preview' | 'plan_ack' | 'plan_progress' | 'plan_step_start' | 'plan_step_complete' | 'execution_done' | 'plan_done' | 'plan_error' | 'plan_cancelled' | 'document_operation' | 'document_operation_complete' | 'section_update' | 'section_delete' | 'document_sync' | 'document_sync_meta' | 'operation_error' | 'text_delta' | 'thinking' | 'diagram_state' | 'delegated' | 'debug_state' | 'debug_chain' | 'document_operation_chunk_start' | 'document_operation_chunk' | 'document_operation_chunk_end' | 'max_tokens_warning';
  message?: string;
  delta?: string;
  tool?: string;
  query?: string;
  edits?: DocumentOperation[];
  maxTurns?: number;
  suggestion?: string;  // For max_tokens_warning - suggestion to continue
  truncated?: boolean;  // For done - indicates response was truncated
  stop_reason?: 'end_turn' | 'user_cancelled' | 'max_turns' | 'error';  // For done - why the session ended
  metadata?: {  // For done with user_cancelled - completed work info
    completed_turns?: number;
    completed_tools?: string[];
  };
  chunkCount?: number | null;
  turn?: number;
  tools?: string[];
  conversationId?: string;
  title?: string;
  jobId?: string;
  step?: number;
  total?: number;
  description?: string;
  currentStep?: number;
  explanation?: string;  // For edit_preview
  operationCount?: number;  // For edit_preview and document_operation_complete
  editType?: 'json' | 'html';  // For edit_preview
  operation?: DocumentOperation;  // For document_operation
  partial?: boolean;  // For document_operation
  // NEW: Server-side operation results
  sectionId?: string;  // For section_update, section_delete
  section?: AeroSection;  // For section_update - the complete section after operation
  operationType?: string;  // For section_update - which operation was applied
  changedBlockIds?: string[];  // For section_update - specific blocks that changed (for targeted highlighting)
  document?: AeroDocument;  // For document_sync - complete document
  version?: string;  // For document_sync/document_sync_meta - new document version
  s3VersionId?: string;  // For document_sync/document_sync_meta - new S3 version ID
  preAIVersionId?: string;  // For document_sync - S3 version ID before AI edits (for undo)
  sectionCount?: number;  // For document_sync_meta - total sections in document for sync verification
  // Thinking events
  thought?: string;  // For thinking - the thought/reasoning text
  toolName?: string;  // For thinking - which tool triggered the thought
  icon?: string;  // For thinking - icon to display
  // Diagram state events
  devices?: DiagramState['devices'];  // For diagram_state - devices/connectors
  wires?: DiagramState['wires'];  // For diagram_state - wires
  crossings?: DiagramState['crossings'];  // For diagram_state - wire crossings
  // Worker delegation events
  connectionId?: string;  // For delegated - WebSocket connection ID
  chainCount?: number;  // For debug_state/debug_chain - number of worker chains
  turnCount?: number;  // For debug_state - current turn count
  elapsedMs?: number;  // For debug_state - time since worker started
  timeoutMs?: number;  // For debug_state - chain timeout threshold
  reason?: string;  // For debug_chain - why chaining occurred (timeout/forced)
  // Chunked message metadata (for large payloads > 100KB)
  _chunked?: {
    id: string;           // Unique ID for this chunk sequence
    index?: number;       // Chunk index (0-based)
    total: number;        // Total number of chunks
    isFirst?: boolean;    // Is this the first chunk?
    isLast?: boolean;     // Is this the last chunk?
    content?: string;     // Chunk content (for document_operation chunks)
    operationType?: string; // Original operation type (for document_operation chunks)
  };
}

export interface PlanStep {
  id: string;
  title: string;
  description?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface StreamHandlers {
  onInit?: (event: StreamEvent) => void;
  onProgress?: (message: string) => void;
  onToolStart?: (tool: string, query?: string) => void;
  onToolComplete?: (tool: string) => void;
  onContent?: (delta: string) => void;
  onWarning?: (message: string) => void;
  onError?: (message: string) => void;
  onMaxTokensWarning?: (message: string, suggestion?: string) => void;  // Response truncated - preserve content, show warning
  onDone?: (message: string, edits?: DocumentOperation[], tokenUsage?: { inputTokens: number; outputTokens: number; totalTokens: number }, truncated?: boolean, stopReason?: 'end_turn' | 'user_cancelled' | 'max_turns' | 'error', resolvedModel?: { modelId: string; reason: string }) => void;
  onHandoff?: (message: string) => void;  // Handoff to background processing
  onEditPreview?: (explanation: string, operationCount?: number, editType?: 'json' | 'html') => void;  // Edit operation preview
  onDocumentOperation?: (operation: DocumentOperation, partial: boolean) => void;  // Stream individual operations (legacy)
  onDocumentOperationComplete?: (operationCount: number) => void;  // All operations sent (legacy)
  // NEW: Server-side operation handlers
  // Note: documentNumber parameter enables cross-document editing (e.g., Plan mode editing target document)
  onSectionUpdate?: (sectionId: string, section: AeroSection, operationType: string, changedBlockIds?: string[], documentNumber?: string) => void;  // Section updated server-side
  onSectionDelete?: (sectionId: string, documentNumber?: string) => void;  // Section deleted server-side
  onDocumentSync?: (document: AeroDocument, version: string, s3VersionId?: string, preAIVersionId?: string, documentNumber?: string) => void;  // Full document sync with version IDs for undo
  onDocumentSyncMeta?: (version: string, s3VersionId?: string, operationCount?: number, sectionCount?: number, documentNumber?: string) => void;  // Lightweight sync for large docs
  onOperationError?: (error: string, operation: DocumentOperation) => void;  // Operation failed
  onTitleUpdate?: (conversationId: string, title: string) => void;
  onTextDelta?: (delta: string) => void;  // Real-time text streaming from Claude
  onClose?: () => void;
  onPlanAck?: (jobId: string, message: string) => void;
  onPlanProgress?: (message: string, currentStep?: number) => void;
  onPlanStepStart?: (step: number, total: number, description: string) => void;
  onPlanStepComplete?: (step: number) => void;
  onPlanDone?: (message: string, edits?: DocumentOperation[]) => void;
  onPlanError?: (message: string) => void;
  onPlanCancelled?: (message: string) => void;
  onPlanDocumentUpdate?: (update: { action: string; planTitle?: string; step?: PlanStep; stepId?: string; stepOrder?: string[]; explanation?: string }) => void;
  onThinking?: (thought: string, toolName?: string, icon?: string, phase?: string) => void;
  onDiagramState?: (state: DiagramState) => void;
  onMermaidUpdate?: (update: { mermaidDiagramId: string; svgUrl: string; title?: string; mermaidType?: string; version: number }) => void;
  // Worker delegation handlers
  onDelegated?: (jobId: string, message: string, connectionId?: string) => void;
  onDebugState?: (state: { jobId: string; turnCount: number; chainCount: number; elapsedMs: number; timeoutMs: number }) => void;
  onDebugChain?: (data: { jobId: string; chainCount: number; turnCount: number; reason: string }) => void;
  // Develop feature autonomous loop handlers
  onDevelopProgress?: (data: { jobId: string; phase: string; iteration: number; maxIterations: number; message: string; success?: boolean; errorCount?: number; errorType?: string }) => void;
  onDevelopComplete?: (data: { jobId: string; result: { success?: boolean; filesCreated?: string[]; filesModified?: string[]; previewUrl?: string | null; pauseReason?: string; totalIterations?: number }; message: string }) => void;
  onDevelopPaused?: (data: { jobId: string; reason: string; message: string; errors: string[] }) => void;
  // Micro-unit (requirements-based micro-coding) handlers
  onMicroUnitProgress?: (data: { jobId: string; reqId?: string; phase: string; iteration?: number; message: string; success?: boolean }) => void;
  onMicroUnitComplete?: (data: { jobId: string; reqId?: string; message: string; result?: Record<string, unknown> }) => void;
  onMicroUnitPaused?: (data: { jobId: string; reqId?: string; reason: string; message: string; errors?: string[] }) => void;
}

/**
 * Read SSE stream and invoke handlers for each event type
 */
export async function readSSEStream(
  response: Response,
  handlers: StreamHandlers
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    let done = false;
    while (!done) {
      const result = await reader.read();
      done = result.done;
      if (done) break;
      const value = result.value;

      // Accumulate chunks
      buffer += decoder.decode(value, { stream: true });
      
      // Split by double newline (SSE event delimiter)
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete chunk in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          let data: StreamEvent;
          try {
            data = JSON.parse(line.slice(6));
          } catch (parseError) {
            logger.error('[SSE] JSON parse error, skipping event:', {
              line: line.substring(0, 200),
              error: parseError
            });
            continue; // Skip this event, continue with next
          }

          try {
            switch (data.type) {
              case 'init':
                handlers.onInit?.(data);
                break;
                
              case 'progress':
                handlers.onProgress?.(data.message || '');
                break;
                
              case 'tool_start':
                handlers.onToolStart?.(data.tool || '', data.query);
                break;
                
              case 'tool_complete':
                handlers.onToolComplete?.(data.tool || '');
                break;
                
              case 'content':
                handlers.onContent?.(data.delta || '');
                break;
                
              case 'text_delta':
                // Real-time text streaming from Claude - forward to handler
                handlers.onTextDelta?.(data.delta || '');
                break;
                
              case 'warning':
                handlers.onWarning?.(data.message || '');
                break;
                
              case 'max_tokens_warning':
                // Response was truncated - preserve already-streamed content, show warning
                logger.debug('[SSE] max_tokens_warning: Response truncated, preserving content');
                handlers.onMaxTokensWarning?.(data.message || 'Response reached length limit', data.suggestion);
                // Also call onWarning so existing warning handlers see it
                handlers.onWarning?.(data.message || 'Response reached length limit');
                break;
                
              case 'error':
                handlers.onError?.(data.message || 'Unknown error');
                break;
                
              case 'done':
                handlers.onDone?.(data.message || '', data.edits, data.tokenUsage, data.truncated, data.stop_reason, data.resolvedModel);
                break;
                
              case 'handoff':
                handlers.onHandoff?.(data.message || 'Handing off to background processing...');
                break;
                
              case 'edit_preview':
                handlers.onEditPreview?.(data.explanation || '', data.operationCount, data.editType);
                break;
                
              case 'document_operation':
                handlers.onDocumentOperation?.(data.operation, data.partial || false);
                break;
                
              case 'document_operation_complete':
                handlers.onDocumentOperationComplete?.(data.operationCount || 0);
                break;
              
              // NEW: Server-side operation events
              // Note: documentNumber enables cross-document editing routing
              case 'section_update':
                if (data.sectionId && data.section) {
                  handlers.onSectionUpdate?.(data.sectionId, data.section, data.operationType || 'unknown', data.changedBlockIds, data.documentNumber);
                }
                break;
                
              case 'section_delete':
                if (data.sectionId) {
                  handlers.onSectionDelete?.(data.sectionId, data.documentNumber);
                }
                break;
                
              case 'document_sync':
                if (data.document) {
                  handlers.onDocumentSync?.(data.document, data.version || '', data.s3VersionId, data.preAIVersionId, data.documentNumber);
                }
                break;
                
              case 'document_sync_meta':
                handlers.onDocumentSyncMeta?.(data.version || '', data.s3VersionId, data.operationCount, data.sectionCount, data.documentNumber);
                break;
                
              case 'operation_error':
                handlers.onOperationError?.(data.message || 'Operation failed', data.operation);
                break;
                
              case 'title_update':
                if (data.conversationId && data.title) {
                  handlers.onTitleUpdate?.(data.conversationId, data.title);
                }
                break;
                
              case 'keepalive':
                // Ignore keepalive messages
                break;
                
              case 'plan_ack':
                handlers.onPlanAck?.(data.jobId || '', data.message || '');
                break;
                
              case 'plan_progress':
                handlers.onPlanProgress?.(data.message || '', data.currentStep);
                break;
                
              case 'plan_step_start':
                handlers.onPlanStepStart?.(data.step || 0, data.total || 0, data.description || '');
                break;
                
              case 'plan_step_complete':
                handlers.onPlanStepComplete?.(data.step || 0);
                break;
                
              case 'execution_done':
              case 'plan_done':  // backward compat
                handlers.onPlanDone?.(data.message || '', data.edits);
                break;
                
              case 'plan_error':
                handlers.onPlanError?.(data.message || 'Unknown plan error');
                break;
                
              case 'plan_cancelled':
                handlers.onPlanCancelled?.(data.message || 'Plan cancelled');
                break;
                
              case 'plan_document_update':
                handlers.onPlanDocumentUpdate?.({
                  action: data.action,
                  planTitle: data.planTitle,
                  step: data.step,
                  stepId: data.stepId,
                  stepOrder: data.stepOrder,
                  explanation: data.explanation
                });
                break;
                
              case 'thinking':
                handlers.onThinking?.(data.thought, data.toolName, data.icon, data.phase);
                break;
                
              case 'diagram_state':
                logger.debug('[SSE] diagram_state event received:', {
                  deviceCount: data.devices?.length,
                  wireCount: data.wires?.length,
                  version: data.version,
                  hasHandler: !!handlers.onDiagramState
                });
                try {
                  handlers.onDiagramState?.({
                    devices: data.devices || [],
                    wires: data.wires || [],
                    crossings: data.crossings || [],
                    version: data.version || Date.now(), // Use server version or generate client-side
                  });
                } catch (handlerError) {
                  logger.error('[SSE] diagram_state handler error:', handlerError);
                }
                break;
                
              case 'mermaid_update':
                logger.debug('[SSE] mermaid_update event received:', {
                  mermaidDiagramId: data.mermaidDiagramId,
                  svgUrl: data.svgUrl?.substring(0, 50) + '...',
                  version: data.version,
                  hasHandler: !!handlers.onMermaidUpdate
                });
                try {
                  handlers.onMermaidUpdate?.({
                    mermaidDiagramId: data.mermaidDiagramId,
                    svgUrl: data.svgUrl,
                    title: data.title,
                    mermaidType: data.mermaidType,
                    version: data.version || Date.now(),
                  });
                } catch (handlerError) {
                  logger.error('[SSE] mermaid_update handler error:', handlerError);
                }
                break;
                
              // Worker delegation events
              case 'delegated':
                logger.debug(`[SSE] Job delegated: ${data.jobId}`);
                handlers.onDelegated?.(data.jobId || '', data.message || 'Processing in background...', data.connectionId);
                break;
                
              case 'debug_state':
                logger.debug(`[SSE Debug] Job ${data.jobId}: turn=${data.turnCount}, chain=${data.chainCount}, elapsed=${data.elapsedMs}ms`);
                handlers.onDebugState?.({
                  jobId: data.jobId || '',
                  turnCount: data.turnCount || 0,
                  chainCount: data.chainCount || 0,
                  elapsedMs: data.elapsedMs || 0,
                  timeoutMs: data.timeoutMs || 0,
                });
                break;
                
              case 'debug_chain':
                logger.debug(`[SSE Debug] Chain triggered: ${data.reason}, chain #${data.chainCount}`);
                handlers.onDebugChain?.({
                  jobId: data.jobId || '',
                  chainCount: data.chainCount || 0,
                  turnCount: data.turnCount || 0,
                  reason: data.reason || 'unknown',
                });
                break;
            }
          } catch (switchError) {
            logger.error('[SSE] Event handler error, continuing:', {
              eventType: data?.type,
              error: switchError
            });
            // Continue processing next events
          }
        }
      }
    }
  } catch (streamError: unknown) {
    // AbortError is expected when user clicks Stop - don't treat as error
    if (streamError instanceof Error && streamError.name === 'AbortError') {
      logger.debug('[Streaming] Stream aborted by user');
      return;
    }
    logger.error('Stream reading error:', streamError);
    throw streamError;
  }
}

/**
 * Make streaming fetch request to AI endpoint
 */
export async function streamingAIRequest(
  url: string,
  body: Record<string, unknown>,
  token: string,
  handlers: StreamHandlers
): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/event-stream', // Request streaming
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401) {
      logger.error('[Streaming] Authentication failed - token may have expired');
      handlers.onError?.('Session expired. Please refresh the page and try again.');
      throw new Error('Authentication failed - session expired');
    }
    if (response.status === 403) {
      logger.error('[Streaming] Access forbidden');
      handlers.onError?.('Access denied. Check your permissions.');
      throw new Error('Access forbidden');
    }
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  // Check if we got a streaming response
  const contentType = response.headers.get('content-type') || '';
  
  if (contentType.includes('text/event-stream')) {
    // Stream response
    await readSSEStream(response, handlers);
  } else {
    // Regular JSON response (fallback)
    const data = await response.json();
    
    // Simulate streaming for backwards compatibility
    handlers.onProgress?.('Processing...');
    handlers.onContent?.(data.data?.message || data.message || '');
    handlers.onDone?.(data.data?.message || data.message || '', data.data?.edits);
  }
}

export function deriveWebSocketUrl(): string | null {
  if (CONFIGURED_WS_URL) {
    return CONFIGURED_WS_URL;
  }

  const apiHostKey = `${API_BASE_URL.replace(/^https?:\/\//, '')}`;
  if (DEV_WS_FALLBACKS[apiHostKey]) {
    return DEV_WS_FALLBACKS[apiHostKey];
  }

  if (!API_BASE_URL || API_BASE_URL === '/api') {
    return null;
  }

  try {
    const httpUrl = new URL(API_BASE_URL);
    const protocol = httpUrl.protocol === 'https:' ? 'wss:' : httpUrl.protocol === 'http:' ? 'ws:' : httpUrl.protocol;
    return `${protocol}//${httpUrl.host}${httpUrl.pathname}`;
  } catch (error) {
    logger.warn('Failed to derive WebSocket URL from API base', error);
    return null;
  }
}

function _websocketAIRequest(
  wsUrl: string,
  body: Record<string, unknown>,
  token: string,
  handlers: StreamHandlers
): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(wsUrl);
    let completed = false;

    const cleanup = (error?: Error) => {
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
      handlers.onClose?.();
      if (error) {
        reject(error);
      } else if (!completed) {
        reject(new Error('Connection closed before completion'));
      } else {
        resolve();
      }
    };

    socket.onopen = () => {
      socket.send(JSON.stringify({
        action: 'start_chat',
        ...body,
        authToken: token,
      }));
    };

    socket.onerror = () => {
      cleanup(new Error('WebSocket error'));
    };

    socket.onclose = () => {
      cleanup();
    };

    socket.onmessage = (event) => {
      try {
        const data: StreamEvent = JSON.parse(event.data);
        switch (data.type) {
          case 'init':
            handlers.onInit?.(data);
            break;
          case 'progress':
            data.message && handlers.onProgress?.(data.message);
            break;
          case 'tool_start':
            handlers.onToolStart?.(data.tool || '', data.query);
            break;
          case 'tool_complete':
            handlers.onToolComplete?.(data.tool || '');
            break;
          case 'content':
            data.delta && handlers.onContent?.(data.delta);
            break;
          case 'warning':
            data.message && handlers.onWarning?.(data.message);
            break;
          case 'error':
            handlers.onError?.(data.message || 'Unknown error');
            completed = true;
            socket.close();
            break;
          case 'done':
            handlers.onDone?.(data.message || '', data.edits, data.tokenUsage, data.truncated, data.stop_reason, data.resolvedModel);
            completed = true;
            socket.close();
            break;
          case 'title_update':
            if (data.conversationId && data.title) {
              handlers.onTitleUpdate?.(data.conversationId, data.title);
            }
            break;
          case 'diagram_state':
            // Handle diagram state updates from plan-worker (diagram mode)
            logger.debug('[WebSocket] diagram_state event received:', {
              deviceCount: data.devices?.length,
              wireCount: data.wires?.length,
              version: data.version,
              hasHandler: !!handlers.onDiagramState
            });
            handlers.onDiagramState?.({
              devices: data.devices || [],
              wires: data.wires || [],
              crossings: data.crossings || [],
              version: data.version || Date.now(),
            });
            break;
          default:
            break;
        }
      } catch (error) {
        logger.error('Failed to parse WebSocket message', event.data, error);
      }
    };
  });
}

/**
 * Make streaming request to Lambda Function URL
 * This is the primary AI streaming method - supports 15-minute operations
 */
async function functionUrlAIRequest(
  functionUrl: string,
  body: Record<string, unknown>,
  token: string,
  handlers: StreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  logger.debug('[Streaming] Using Lambda Function URL for AI streaming');
  
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Include auth token in body (Function URL uses body auth, not headers)
    body: JSON.stringify({
      ...body,
      authToken: token,
    }),
    signal, // Pass abort signal to fetch
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  // Stream response
  await readSSEStream(response, handlers);
}

export interface AIStreamController {
  abort: () => void;
  promise: Promise<void>;
}

export function startAIStream(
  body: Record<string, unknown>,
  token: string,
  handlers: StreamHandlers
): AIStreamController {
  const abortController = new AbortController();
  
  const promise = (async () => {
    try {
      // Lambda Function URL - 15-minute streaming, no timeout issues
      if (AI_FUNCTION_URL) {
        await functionUrlAIRequest(AI_FUNCTION_URL, body, token, handlers, abortController.signal);
        return;
      }

      // Function URL not configured - throw clear error in development
      // Production should always have VITE_AI_FUNCTION_URL set
      logger.error('[Streaming] VITE_AI_FUNCTION_URL not configured');
      throw new Error('AI streaming not configured. Set VITE_AI_FUNCTION_URL environment variable.');
    } catch (error: unknown) {
      if (error.name === 'AbortError') {
        logger.debug('[Streaming] Request aborted by user');
        handlers.onClose?.();
        return;
      }
      throw error;
    }
  })();
  
  return {
    abort: () => {
      logger.debug('[Streaming] Aborting AI stream...');
      abortController.abort();
    },
    promise
  };
}

/**
 * Cancel a running worker job
 * @param jobId - The job ID to cancel
 * @param token - Authentication token
 * @returns Promise with cancel result
 */
export async function cancelJob(jobId: string, token: string): Promise<{ success: boolean; error?: string }> {
  if (!AI_FUNCTION_URL) {
    logger.error('[Streaming] AI_FUNCTION_URL not configured for cancel');
    return { success: false, error: 'AI streaming not configured' };
  }
  
  try {
    const cancelUrl = `${AI_FUNCTION_URL}/jobs/${jobId}/cancel`;
    logger.debug(`[Streaming] Cancelling job ${jobId}...`);
    
    const response = await fetch(cancelUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authToken: token }),
    });
    
    const result = await response.json();
    
    if (result.success) {
      logger.debug(`[Streaming] Job ${jobId} cancelled successfully`);
      return { success: true };
    } else {
      logger.error(`[Streaming] Failed to cancel job ${jobId}:`, result.error);
      return { success: false, error: result.error };
    }
  } catch (error: unknown) {
    logger.error(`[Streaming] Error cancelling job ${jobId}:`, error);
    return { success: false, error: error.message };
  }
}

/**
 * Cancel an AI conversation (graceful stop for non-Agent modes)
 * Sets a cancellation flag that the AI tool loop checks before starting new work.
 * 
 * @param conversationId - The conversation ID to cancel
 * @param token - Authentication token
 * @returns Promise with cancel result
 */
export async function cancelConversation(
  conversationId: string, 
  token: string
): Promise<{ acknowledged: boolean; error?: string }> {
  if (!AI_FUNCTION_URL) {
    logger.error('[Streaming] AI_FUNCTION_URL not configured for cancel');
    return { acknowledged: false, error: 'AI streaming not configured' };
  }
  
  if (!conversationId) {
    return { acknowledged: false, error: 'conversationId required' };
  }
  
  try {
    const cancelUrl = `${AI_FUNCTION_URL}/conversations/${conversationId}/cancel`;
    logger.debug(`[Streaming] Cancelling conversation ${conversationId}...`);
    
    const response = await fetch(cancelUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ authToken: token }),
    });
    
    const result = await response.json();
    
    if (result.acknowledged) {
      logger.debug(`[Streaming] Conversation ${conversationId} cancellation acknowledged`);
      return { acknowledged: true };
    } else {
      logger.error(`[Streaming] Failed to cancel conversation ${conversationId}:`, result.error);
      return { acknowledged: false, error: result.error };
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error(`[Streaming] Error cancelling conversation ${conversationId}:`, error);
    return { acknowledged: false, error: errorMessage };
  }
}
